import { AdControls } from '@post-office/components--ad-controls';
import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePublish } from '@atlaskit/rovo-triggers';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { type JSX } from 'react/jsx-runtime';

import RovoIcon from '../../../../../assets/rovo-icon-nobg-lg.svg';
import TwgGlyph from '../../../../../assets/twg-glyph.svg';
import {
	ROVO_CHAT_NON_MAU_JIRA_CONFLUENCE_CTA_EXPERIMENT,
	ROVO_CHAT_NON_MAU_JIRA_CONFLUENCE_CTA_TREATMENT_PARAM,
	NonMauChatCtaCohort,
} from '../constants';
import { messages, selectCopyForCohort } from '../messages';

const styles = cssMap({
	boxContainer: {
		display: 'flex',
		width: '100%',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		alignItems: 'flex-start',
		gap: token('space.100'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.information'),
		placeContent: 'center',
	},
	contentStack: {
		gap: token('space.100'),
		width: '100%',
	},
	headerRow: {
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	ctaLink: {
		backgroundColor: token('color.background.neutral.subtle'),
		padding: token('space.0'),
		borderRadius: token('radius.xsmall'),
		color: token('color.link'),
		cursor: 'pointer',
		font: token('font.body.small'),
		textAlign: 'left',
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
});

const ACTION_SUBJECT = 'rovoNonMauChatCta';

type NonMauChatCtaMessageProps = {
	context: {
		source: string;
		[key: string]: unknown;
	};
	handleOnDismissed: () => void;
};

export const NonMauChatCtaMessage = ({
	context,
	handleOnDismissed,
}: NonMauChatCtaMessageProps): JSX.Element => {
	const { source } = context;

	// Exposure is already logged in the placements.
	// We call getExperimentValue here only to read the cohort for rendering the correct copy.
	const experimentCohort = FeatureGates.getExperimentValue(
		ROVO_CHAT_NON_MAU_JIRA_CONFLUENCE_CTA_EXPERIMENT,
		ROVO_CHAT_NON_MAU_JIRA_CONFLUENCE_CTA_TREATMENT_PARAM,
		NonMauChatCtaCohort.V1 as NonMauChatCtaCohort,
		{ fireExperimentExposure: false },
	);

	const { formatMessage } = useIntl();
	const { heading, description } = selectCopyForCohort(experimentCohort);
	const { messageClicked } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const chatEventPublish = usePublish('ai-mate');

	const headingText = formatMessage(heading);
	const descriptionText = formatMessage(description);
	const ctaText = formatMessage(messages.ctaLinkText);

	useEffect(() => {
		if (experimentCohort === NonMauChatCtaCohort.CONTROL) return;
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'rendered',
			actionSubject: ACTION_SUBJECT,
			attributes: {
				variant: experimentCohort,
				source,
			},
		});
	}, [fireAnalyticsEvent, experimentCohort, source]);

	const handleDismiss = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'dismissed',
			actionSubject: ACTION_SUBJECT,
			attributes: {
				variant: experimentCohort,
				source,
			},
		});
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source,
			},
		});
		handleOnDismissed();
	}, [
		fireAnalyticsEvent,
		experimentCohort,
		source,
		messageClicked,
		messageInstanceClicked,
		handleOnDismissed,
	]);

	const handleCtaClick = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: ACTION_SUBJECT,
			attributes: {
				variant: experimentCohort,
				source,
			},
		});
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'openRovoChat',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source,
			},
		});
		chatEventPublish({
			type: 'chat-new',
			source: 'rovoNonMauChatCta',
			data: {
				prompt: formatMessage(messages.chatPrompt),
				dialogues: [],
			},
		});
	}, [
		fireAnalyticsEvent,
		experimentCohort,
		source,
		messageClicked,
		messageInstanceClicked,
		chatEventPublish,
	]);

	return (
		<Box xcss={cx(styles.boxContainer)} testId="rovo-non-mau-chat-cta">
			<Stack xcss={cx(styles.contentStack)} testId="rovo-non-mau-chat-cta-content">
				<Inline xcss={cx(styles.headerRow)}>
					<Image
						src={experimentCohort === NonMauChatCtaCohort.V2 ? TwgGlyph : RovoIcon}
						alt=""
						role="presentation"
						width={22}
						height={22}
					/>
					<AdControls
						showWhyAmISeeingThis={true}
						whyAmISeeingThisModalContent={{
							reasons: [
								<FormattedMessage key="reason-1" {...messages.whyAmISeeingThisReason1} />,
								<FormattedMessage key="reason-2" {...messages.whyAmISeeingThisReason2} />,
							],
						}}
						onDismissed={handleDismiss}
						spacing="compact"
					/>
				</Inline>
				<Heading size="xsmall">{headingText}</Heading>
				<Text size="small" color="color.text.subtlest">
					{descriptionText}
				</Text>
				<Pressable
					onClick={handleCtaClick}
					testId="rovo-non-mau-chat-cta-link"
					xcss={cx(styles.ctaLink)}
				>
					{ctaText}
				</Pressable>
			</Stack>
		</Box>
	);
};
